'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ConsultationTypeCard from './ConsultationTypeCard';
import UserTypeSelector from './UserTypeSelector';
import ConsultationForm from './ConsultationForm';
import TimelinePreview from './TimelinePreview';
import ResourceCard from './ResourceCard';
import SuccessModal from './SuccessModal';

interface UserType {
  id: string;
  label: string;
  icon: string;
  description: string;
}

interface ConsultationType {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface TimelineStep {
  title: string;
  description: string;
  duration: string;
}

interface Resource {
  title: string;
  description: string;
  icon: string;
  downloadUrl: string;
  fileSize: string;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  message: string;
}

export default function GetStartedInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedUserType, setSelectedUserType] = useState('');
  const [selectedConsultationType, setSelectedConsultationType] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'en';
    setLanguage(savedLanguage);
  }, []);

  const userTypes: UserType[] = [
    {
      id: 'curious-learner',
      label: 'Curious Learner',
      icon: 'AcademicCapIcon',
      description: 'New to carbon credits and ESG? Start your learning journey here.',
    },
    {
      id: 'enterprise',
      label: 'Enterprise Decision Maker',
      icon: 'BuildingOfficeIcon',
      description: 'Looking for ESG compliance and carbon credit solutions for your business.',
    },
    {
      id: 'project-developer',
      label: 'Project Developer',
      icon: 'CogIcon',
      description: 'Ready to develop carbon credit projects with technical expertise.',
    },
    {
      id: 'compliance-officer',
      label: 'Compliance Officer',
      icon: 'ShieldCheckIcon',
      description: 'Need ESG reporting and regulatory compliance support.',
    },
    {
      id: 'esg-training',
      label: 'ESG Training',
      icon: 'AcademicCapIcon',
      description: 'Comprehensive ESG training programs for organizations and professionals to build sustainability expertise.',
    },
    {
      id: 'carbon-credit-joint-assessment',
      label: 'Carbon Credit Joint Assessment',
      icon: 'ClipboardDocumentCheckIcon',
      description: 'Collaborative assessment and guidance on earning carbon credits through verified sustainability projects.',
    },
  ];

  const consultationTypes: ConsultationType[] = [
    {
      id: 'whatsapp',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'WhatsApp Consultation',
      description: 'Quick chat with our team for immediate questions and guidance.',
    },
    {
      id: 'email',
      icon: 'EnvelopeIcon',
      title: 'Email Consultation',
      description: 'Detailed inquiry sent to dr.nupur@gencarbon.in for comprehensive response.',
    },
    {
      id: 'phone',
      icon: 'PhoneIcon',
      title: 'Phone Call',
      description: 'Schedule a call with our experts to discuss your requirements.',
    },
    {
      id: 'video',
      icon: 'VideoCameraIcon',
      title: 'Video Meeting',
      description: 'Face-to-face virtual consultation with screen sharing capabilities.',
    },
  ];

  const timelineSteps: TimelineStep[] = [
    {
      title: 'Initial Consultation',
      description: 'We review your requirements and schedule a detailed discussion.',
      duration: '1-2 days',
    },
    {
      title: 'Assessment & Planning',
      description: 'Our team conducts a thorough assessment and creates a customized plan.',
      duration: '3-5 days',
    },
    {
      title: 'Proposal & Agreement',
      description: 'Receive detailed proposal with timeline, deliverables, and pricing.',
      duration: '2-3 days',
    },
    {
      title: 'Implementation',
      description: 'Begin project execution with regular progress updates and support.',
      duration: 'Varies by project',
    },
  ];

  const resources: Resource[] = [
    {
      title: 'Carbon Credits Beginner Guide',
      description: 'Comprehensive introduction to carbon credits and how they work.',
      icon: 'DocumentTextIcon',
      downloadUrl: '#',
      fileSize: '2.4 MB',
    },
    {
      title: 'ESG Reporting Checklist',
      description: 'Step-by-step checklist for ESG compliance and reporting.',
      icon: 'ClipboardDocumentCheckIcon',
      downloadUrl: '#',
      fileSize: '1.8 MB',
    },
    {
      title: 'Water Conservation Guide',
      description: 'Best practices for water management and conservation projects.',
      icon: 'BeakerIcon',
      downloadUrl: '#',
      fileSize: '3.1 MB',
    },
  ];

  const handleUserTypeSelect = (typeId: string) => {
    setSelectedUserType(typeId);
    setCurrentStep(2);
  };

  const handleConsultationTypeSelect = (typeId: string) => {
    setSelectedConsultationType(typeId);
    setCurrentStep(3);
  };

  const handleFormSubmit = (formData: FormData) => {
    setSubmittedEmail(formData.email);
    setShowSuccessModal(true);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setCurrentStep(1);
    setSelectedUserType('');
    setSelectedConsultationType('');
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full px-4 md:px-6 lg:px-8 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-surface rounded-lg w-3/4"></div>
              <div className="h-6 bg-surface rounded-lg w-1/2"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-32 bg-surface rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Language Selector */}
        <div className="w-full border-b border-border bg-card">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-end space-x-2">
              <Icon name="LanguageIcon" size={20} className="text-text-secondary" />
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-input bg-card text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="en">English</option>
                <option value="hi">हिंदी (Hindi)</option>
                <option value="ar">العربية (Arabic)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="w-full bg-gradient-to-br from-primary/5 via-surface to-secondary/5 border-b border-border">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-16">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-text-primary mb-4">
                Start Your Sustainability Journey
              </h1>
              <p className="text-lg text-text-secondary mb-8">
                Whether you&apos;re new to carbon credits or ready to implement ESG solutions,
                we&apos;re here to guide you every step of the way.
              </p>

              {/* Progress Indicator */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold transition-all duration-300 ${
                        currentStep >= step
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-surface text-text-secondary'
                      }`}
                    >
                      {step}
                    </div>
                    {step < 3 && (
                      <div
                        className={`w-16 h-1 mx-2 rounded-full transition-all duration-300 ${
                          currentStep > step ? 'bg-primary' : 'bg-border'
                        }`}
                      ></div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
                <Icon name="InformationCircleIcon" size={20} />
                <span>
                  {currentStep === 1 && 'Select your profile type'}
                  {currentStep === 2 && 'Choose consultation method'}
                  {currentStep === 3 && 'Complete your details'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full px-4 md:px-6 lg:px-8 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Form Steps */}
              <div className="lg:col-span-2 space-y-8">
                {/* Step 1: User Type Selection */}
                {currentStep === 1 && (
                  <div className="bg-card rounded-lg border border-border p-8 shadow-natural">
                    <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                      Tell Us About Yourself
                    </h2>
                    <p className="text-text-secondary mb-6">
                      Select the option that best describes your role or interest.
                    </p>
                    <UserTypeSelector
                      userTypes={userTypes}
                      selectedType={selectedUserType}
                      onSelect={handleUserTypeSelect}
                    />
                  </div>
                )}

                {/* Step 2: Consultation Type Selection */}
                {currentStep === 2 && (
                  <div className="bg-card rounded-lg border border-border p-8 shadow-natural">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex items-center space-x-2 text-sm text-primary hover:text-secondary mb-6 transition-colors duration-200"
                    >
                      <Icon name="ArrowLeftIcon" size={16} />
                      <span>Back to profile selection</span>
                    </button>
                    <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                      Choose Your Consultation Method
                    </h2>
                    <p className="text-text-secondary mb-6">
                      Select how you&apos;d like to connect with our team.
                    </p>
                    <div className="space-y-4">
                      {consultationTypes.map((type) => (
                        <ConsultationTypeCard
                          key={type.id}
                          icon={type.icon}
                          title={type.title}
                          description={type.description}
                          isSelected={selectedConsultationType === type.id}
                          onSelect={() => handleConsultationTypeSelect(type.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Consultation Form */}
                {currentStep === 3 && (
                  <div className="bg-card rounded-lg border border-border p-8 shadow-natural">
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="flex items-center space-x-2 text-sm text-primary hover:text-secondary mb-6 transition-colors duration-200"
                    >
                      <Icon name="ArrowLeftIcon" size={16} />
                      <span>Back to consultation method</span>
                    </button>
                    <h2 className="font-heading font-bold text-2xl text-text-primary mb-2">
                      Complete Your Details
                    </h2>
                    <p className="text-text-secondary mb-6">
                      Fill in your information so we can provide personalized assistance.
                    </p>
                    <ConsultationForm
                      userType={selectedUserType}
                      onSubmit={handleFormSubmit}
                    />
                  </div>
                )}
              </div>

              {/* Right Column - Timeline & Resources */}
              <div className="space-y-6">
                {/* Expected Timeline */}
                <div className="bg-card rounded-lg border border-border p-6 shadow-natural">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="ClockIcon" size={24} className="text-primary" />
                    <h3 className="font-heading font-bold text-xl text-text-primary">
                      What to Expect
                    </h3>
                  </div>
                  <TimelinePreview steps={timelineSteps} />
                </div>

                {/* Quick Resources */}
                <div className="bg-card rounded-lg border border-border p-6 shadow-natural">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                    <h3 className="font-heading font-bold text-xl text-text-primary">
                      Free Resources
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {resources.map((resource, index) => (
                      <ResourceCard key={index} {...resource} />
                    ))}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg border border-primary/20 p-6">
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-4">
                    Need Immediate Assistance?
                  </h3>
                  <div className="space-y-3">
                    <a
                      href="mailto:dr.nupur@gencarbon.in"
                      className="flex items-center space-x-3 text-sm text-text-primary hover:text-primary transition-colors duration-200"
                    >
                      <Icon name="EnvelopeIcon" size={20} className="text-primary" />
                      <span>dr.nupur@gencarbon.in</span>
                    </a>
                    <a
                      href="https://wa.me/919901503669"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-sm text-text-primary hover:text-primary transition-colors duration-200"
                    >
                      <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-[#25D366]" />
                      <span>WhatsApp: +91-9901503669</span>
                    </a>
                    <div className="flex items-center space-x-3 text-sm text-text-primary">
                      <Icon name="ClockIcon" size={20} className="text-primary" />
                      <span>Mon-Fri: 9:00 AM - 6:00 PM IST</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="w-full bg-surface border-t border-border">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-12">
            <div className="bg-gradient-to-br from-primary to-secondary rounded-lg p-8 md:p-12 text-center">
              <h2 className="font-heading font-bold text-3xl text-primary-foreground mb-4">
                Stay Updated on Sustainability
              </h2>
              <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
                Subscribe to our newsletter for the latest insights on carbon credits, ESG
                reporting, and environmental impact.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg border border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground placeholder-primary-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary-foreground/50"
                />
                <button className="w-full sm:w-auto px-6 py-3 bg-primary-foreground text-primary font-heading font-semibold rounded-lg hover:bg-primary-foreground/90 shadow-natural transition-all duration-300 whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        consultationType={
          consultationTypes.find((t) => t.id === selectedConsultationType)?.title || ''
        }
        userEmail={submittedEmail}
      />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919901503669?text=Hi, I'd like to learn more about GenCarbon ESG services."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] text-white rounded-full shadow-natural-lg hover:shadow-natural-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-40"
        aria-label="Chat on WhatsApp"
      >
        <Icon name="ChatBubbleLeftRightIcon" size={28} />
      </a>
    </>
  );
}